"use client";

import { usePathname } from "next/navigation";
import Link from "next/link";
import { useLanguageSwitcher } from "@contexts/LanguageSwitcherContext";
import { getAlternateUrl, getLocaleFromPathname, type Locale } from "@utils/i18n/config";

/**
 * Language switcher that appears in the header.
 *
 * Target URL priority:
 * 1. DE -> EN always lands on `/en` (explicit product requirement).
 * 2. Otherwise, use Polylang translation links from LanguageSwitcherContext
 *    when available (used mainly for EN blog posts switching back to DE).
 * 3. Fallback to path-based mapping via getAlternateUrl().
 */
export default function LanguageSwitcher() {
  const pathname = usePathname();
  const { translatedUrls } = useLanguageSwitcher();
  const currentLocale = getLocaleFromPathname(pathname);

  function getTargetUrl(targetLocale: Locale): string {
    if (targetLocale === "en" && currentLocale === "de") {
      return "/en";
    }

    const contextLink = translatedUrls.find(
      (u) => u.lang === (targetLocale === "en" ? "en-US" : "de") || u.lang === targetLocale
    );
    if (contextLink) return contextLink.href;

    return getAlternateUrl(pathname, targetLocale);
  }

  const deUrl = getTargetUrl("de");
  const enUrl = getTargetUrl("en");

  return (
    <div className="lang-switcher" role="navigation" aria-label="Language selection">
      <Link
        href={deUrl}
        aria-label="Deutsch"
        aria-current={currentLocale === "de" ? "true" : undefined}
        className={`lang-switch-btn ${currentLocale === "de" ? "lang-switch-btn--active" : ""}`}
      >
        DE
      </Link>
      <span className="lang-switcher__separator" aria-hidden="true" />
      <Link
        href={enUrl}
        aria-label="English"
        aria-current={currentLocale === "en" ? "true" : undefined}
        className={`lang-switch-btn ${currentLocale === "en" ? "lang-switch-btn--active" : ""}`}
      >
        EN
      </Link>
    </div>
  );
}
